import React from 'react';
import type { ReactNode } from 'react';
import { StyleSheet } from 'react-native';

import { RightChevron } from '@/assets/icons/right';
import { Text } from '@/components/TextComponent';
import type { TextProps } from '@/components/TextComponent';
import { Pressable } from '@/components/button/Pressable';
import { Box, BoxProps, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { common } from '@/styles/styles';

import { SafetyShadow } from './SafetyShadow';

export type CurrencyTagLabel = 'Betr Bucks' | 'Play-Through';

export type EntryCardPayoutProps = {
    entryLabel: string;
    entryAmount: string;
    /** Currency icon beside the entry amount — green cash, or the purple Betr Bucks icon for a bonus entry. */
    entryIcon?: ReactNode;
    /** Optional pill beside the entry amount (e.g. "Betr Bucks") indicating how the entry was funded. */
    entryTag?: CurrencyTagLabel;
    payoutLabel: string;
    payoutAmount: string;
    /** Currency icon beside the payout amount — independent of the entry icon (e.g. a bonus payout uses purple). */
    payoutIcon?: ReactNode;
    /** Optional pill beside the payout amount (e.g. "Betr Bucks" for a No Sweat refund paid as site credit). */
    payoutTag?: CurrencyTagLabel;
    payoutTagBackgroundColor?: BoxProps['backgroundColor'];
    payoutColor?: TextProps['color'];
    onPress?: () => void;
};

const CurrencyTag = ({
    label,
    backgroundColor = ds.colorToken.background.brand.emphasis,
}: {
    label: CurrencyTagLabel;
    backgroundColor?: BoxProps['backgroundColor'];
}) => (
    <Box
        backgroundColor={backgroundColor}
        borderRadius="r7"
        paddingHorizontal="s6"
        paddingVertical="s4"
        style={common.borderCurve}
    >
        <Text variant="labelMedium" color={ds.colorToken.text.primary} fontWeight={600} numberOfLines={1}>
            {label}
        </Text>
    </Box>
);

export const EntryCardPayout = ({
    entryLabel,
    entryAmount,
    entryIcon,
    entryTag,
    payoutLabel,
    payoutAmount,
    payoutIcon,
    payoutTag,
    payoutTagBackgroundColor,
    payoutColor = ds.colorToken.text.primary,
    onPress,
}: EntryCardPayoutProps) => {
    return (
        <Row paddingLeft="s16" paddingRight="s4" alignItems="center">
            <Row flex={1} alignItems="center" gap="s20">
                <Column justifyContent="center">
                    <Text variant="bodySmall2" color="neutral300">
                        {entryLabel}
                    </Text>
                    <Row alignItems="center" gap="s8">
                        <Row alignItems="center" gap="s6">
                            {entryIcon}
                            <Text variant="headlineSmall2" color={ds.colorToken.text.primary} numberOfLines={1}>
                                {entryAmount}
                            </Text>
                        </Row>
                        {entryTag ? <CurrencyTag label={entryTag} /> : null}
                    </Row>
                </Column>
                <Box width={1} height={40} backgroundColor={ds.colorToken.border.strong} alignSelf="center" />
                {/* Payout can be arbitrarily large; it grows, clips, and fades under the safety shadow. */}
                <Column flex={1} justifyContent="center" overflow="hidden">
                    <Text variant="bodySmall2" color="neutral300">
                        {payoutLabel}
                    </Text>
                    <Row alignItems="center" gap="s8">
                        <Row alignItems="center" gap="s6" flexShrink={1}>
                            {payoutIcon}
                            <Text
                                variant="headlineSmall2"
                                color={payoutColor}
                                numberOfLines={1}
                                ellipsizeMode="clip"
                                flexShrink={1}
                            >
                                {payoutAmount}
                            </Text>
                        </Row>
                        {payoutTag ? (
                            <CurrencyTag label={payoutTag} backgroundColor={payoutTagBackgroundColor} />
                        ) : null}
                    </Row>
                    <SafetyShadow style={styles.shadow} />
                </Column>
            </Row>
            <Pressable onPress={onPress} disabled={!onPress} accessibilityRole="button">
                <Box width={48} height={48} alignItems="center" justifyContent="center">
                    {/* RightIcon has no viewBox, so it can't be scaled — render it at its native 8x14, centered. */}
                    <RightChevron />
                </Box>
            </Pressable>
        </Row>
    );
};

const styles = StyleSheet.create({
    shadow: { position: 'absolute', top: 0, right: 0, bottom: 0, width: 32 },
});
